import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

export default class extends Controller {
  static values = {
    data: Object,
    chartType: { type: String, default: "line" },
    currency: { type: Boolean, default: true }
  }

  connect() {
    const ctx = this.element.getContext("2d")
    this.chart = new Chart(ctx, this.buildConfig())
    this._onToggle = this.handleToggle.bind(this)
    document.addEventListener("toggle-geography", this._onToggle)
    this._onBeforeCache = this.teardownForCache.bind(this)
    document.addEventListener("turbo:before-cache", this._onBeforeCache)
  }

  teardownForCache() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  handleToggle(event) {
    const { index, visible } = event.detail
    if (index >= this.chart.data.datasets.length) return
    this.chart.setDatasetVisibility(index, visible)
    this.chart.update()
  }

  buildConfig() {
    const isBar = this.chartTypeValue === "bar"
    const multiDataset = this.dataValue.datasets.length > 1

    const datasets = this.dataValue.datasets.map(ds => ({
      label: ds.name,
      data: ds.values,
      borderColor: ds.color,
      backgroundColor: isBar ? ds.color + "b3" : ds.color + "1a",
      fill: !isBar && !multiDataset,
      tension: 0.3,
      pointRadius: multiDataset ? 2 : 3,
      borderWidth: 2,
      borderRadius: isBar ? 4 : 0,
      spanGaps: true
    }))

    return {
      type: this.chartTypeValue,
      data: {
        labels: this.dataValue.labels,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed.y
                if (val == null) return null
                const formatted = this.currencyValue ? `$${val.toLocaleString()}` : val.toLocaleString()
                return `${ctx.dataset.label}: ${formatted}`
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: isBar,
            ticks: {
              callback: (val) => this.currencyValue ? "$" + val.toLocaleString() : val.toLocaleString(),
              font: { size: 10 },
              maxTicksLimit: 6
            }
          },
          x: {
            ticks: {
              font: { size: 10 },
              maxRotation: 45,
              maxTicksLimit: 10
            }
          }
        }
      }
    }
  }

  disconnect() {
    document.removeEventListener("toggle-geography", this._onToggle)
    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }
}
